import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbInfoCircle } from "react-icons/tb";
import { useLayoutContext } from "../../../Contexts/LayoutContext";

interface DescriptionProps {
    description: string;
    wordLimit: number;
    className?: string;
}

const Description: React.FC<DescriptionProps> = ({
    description,
    wordLimit,
    className
}) => {
    const [expanded, setExpanded] = useState(false);
    const { isMobile } = useLayoutContext();
    const { t } = useTranslation();

    const words = useMemo<string[]>(() => {
        return description?.trim().split(/\s+/);
    }, [description]);

    const hasOverflow = words?.length > wordLimit;

    const displayedText = useMemo(() => {
        if (!hasOverflow || expanded) {
            return description;
        }

        return words?.slice(0, wordLimit).join(" ") + "...";
    }, [description, words, wordLimit, hasOverflow, expanded]);

    if (!description || !words) {
        return null;
    }

    return (
        <div className={`flex gap-1.5 text-gray-400 italic p-1 leading-4 md:leading-5 ${className}`}>
            <div className="flex flex-col items-center gap-1 md:pt-[.05em]">
                <div><TbInfoCircle size={isMobile ? 16 : 18} /></div>
                <div className="w-0.5 h-full rounded-full bg-gradient-to-b from-gray-200 to-white"></div>
            </div>
            <div className=" text-justify">
                <span className="mr-1.5">{displayedText}</span>

                {hasOverflow && (
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        className="font-medium text-gray-900/50 italic"
                        >
                        {expanded ? t('layout.description.hide_label') : t('layout.description.show_label')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default Description;